import { Component } from 'react';

import { captureError } from 'services/monitoring';

/**
 * ErrorBoundary — catches render-time crashes in the subtree and shows a
 * themed fallback instead of a white screen.
 *
 * Used at two levels:
 *   • app root (AppProviders) — full-screen catch-all for the whole tree.
 *   • per route (DashboardShell, keyed by pathname) — contains a single page's
 *     crash inside the dashboard chrome and recovers automatically on navigation
 *     (a new key remounts the boundary).
 *
 * @param {'screen'|'inline'} [variant='screen']  Layout of the fallback.
 * @param {node}              [children]
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // `variant` tells us which boundary caught it: 'inline' is one page failing
    // inside the dashboard, 'screen' is the whole tree going down — a much
    // louder signal for the same stack trace.
    captureError(error, {
      componentStack: info?.componentStack,
      boundary: this.props.variant || 'screen',
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
    // eslint-disable-next-line no-console
    console.error('Uncaught error:', error, info);
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    const { variant = 'screen' } = this.props;
    const inline = variant === 'inline';

    return (
      <div
        role="alert"
        className={
          inline
            ? 'flex flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-white px-6 py-16 text-center shadow-card'
            : 'flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 p-6 text-center'
        }
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-danger ring-1 ring-inset ring-red-200">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 9v4m0 4h.01M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <h1 className="font-display text-xl font-semibold text-slate-900">Something went wrong</h1>
        <p className="max-w-md text-sm text-slate-500">
          An unexpected error occurred. You can try again, or reload the page.
        </p>

        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={this.handleReset}
            className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700 focus-ring"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-ring"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
